import { GrowthBook } from "@growthbook/growthbook";
import type { EvaluationContext, Provider, ProviderMetadata, ResolutionDetails } from "@openfeature/web-sdk";

/**
 * Hand-rolled OpenFeature web provider wrapping GrowthBook's actively-maintained
 * `@growthbook/growthbook` JS SDK directly.
 *
 * GrowthBook does not publish an official or well-maintained browser/web
 * OpenFeature provider (only Python/Go/.NET/Java have first-party support;
 * the community `@openfeature/growthbook-client-provider` package is ~2 years
 * stale and unmaintained). GrowthBook's own docs explicitly document this
 * "build your own provider" pattern for unsupported platforms.
 *
 * The OpenFeature web SDK manages provider readiness itself: it calls
 * `initialize()` once after `OpenFeature.setProvider(...)`, and fires
 * `ProviderEvents.Ready` when the returned promise resolves (or
 * `ProviderEvents.Error` if it rejects) — this provider does not need to
 * emit those events manually.
 *
 * Only boolean flag resolution is implemented, since that's all this widget
 * currently evaluates. String/number/object resolution methods are present
 * to satisfy the OpenFeature `Provider` interface, but simply return the
 * caller-supplied default.
 */
export class GrowthBookOpenFeatureProvider implements Provider {
  readonly metadata: ProviderMetadata = { name: "growthbook-custom-web-provider" };

  private readonly growthbook: GrowthBook;

  constructor(clientKey: string, apiHost?: string) {
    this.growthbook = new GrowthBook({
      clientKey,
      ...(apiHost ? { apiHost } : {}),
    });
  }

  async initialize(): Promise<void> {
    await this.growthbook.init({ streaming: false });
  }

  async onClose(): Promise<void> {
    this.growthbook.destroy();
  }

  resolveBooleanEvaluation(flagKey: string, defaultValue: boolean): ResolutionDetails<boolean> {
    if (!this.growthbook.ready) {
      return { value: defaultValue, reason: "DEFAULT" };
    }
    return { value: this.growthbook.isOn(flagKey), reason: "TARGETING_MATCH" };
  }

  resolveStringEvaluation(_flagKey: string, defaultValue: string): ResolutionDetails<string> {
    return { value: defaultValue, reason: "DEFAULT" };
  }

  resolveNumberEvaluation(_flagKey: string, defaultValue: number): ResolutionDetails<number> {
    return { value: defaultValue, reason: "DEFAULT" };
  }

  resolveObjectEvaluation<T>(_flagKey: string, defaultValue: T): ResolutionDetails<T> {
    return { value: defaultValue, reason: "DEFAULT" };
  }

  onContextChange(_oldContext: EvaluationContext, _newContext: EvaluationContext): void {
    // No-op: this widget only ever evaluates flags with a fixed sentinel
    // targeting key ("global"); there is no per-request context to reconcile.
  }
}
